/**
 * CommandPalette — ⌘K fuzzy search.
 * Searches the NAV registry for tab navigation. Future enhancement: hit
 * /api/v1/admin/search?q= for global lead search across all sources.
 */
import { useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { NAV } from './nav';

export default function CommandPalette({ onClose, onNav, role }) {
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);
  const inputRef = useRef();

  useEffect(() => {
    inputRef.current && inputRef.current.focus();
  }, []);

  const tabs = [];
  for (const sec of NAV) {
    for (const it of sec.items) {
      if (it.role === 'any' || role === 'admin') tabs.push(it);
    }
  }

  const ql = q.toLowerCase();
  const navHits = tabs
    .filter((t) => t.label.toLowerCase().includes(ql))
    .map((t) => ({
      key: `nav-${t.key}`,
      Icon: t.Icon,
      label: `Go to ${t.label}`,
      run: () => onNav(t.key),
    }));

  const groups = [{ title: 'Navigate', items: navHits }].filter((g) => g.items.length);
  const flat = (groups || []).flatMap((g) => g.items);

  useEffect(() => {
    setIdx(0);
  }, [q]);

  const run = (it) => {
    it.run();
    onClose();
  };

  const onKey = (ev) => {
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      setIdx((i) => Math.min(i + 1, flat.length - 1));
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      setIdx((i) => Math.max(i - 1, 0));
    } else if (ev.key === 'Enter') {
      ev.preventDefault();
      flat[idx] && run(flat[idx]);
    } else if (ev.key === 'Escape') {
      onClose();
    }
  };

  let running = -1;
  return (
    <div
      className="scrim"
      onMouseDown={onClose}
      data-testid="command-palette"
    >
      <div data-testid="command-palette-div-1" className="cmdk" onMouseDown={(ev) => ev.stopPropagation()}>
        <div className="cmdk-search">
          <Search size={18} style={{ color: 'var(--muted)' }} />
          <input
            ref={inputRef}
            value={q}
            placeholder="Search or jump to…"
            onChange={(ev) => setQ(ev.target.value)}
            onKeyDown={onKey}
            data-testid="cmdk-input"
          />
          <span className="kbd">Esc</span>
        </div>
        <div className="cmdk-list">
          {groups.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)' }}>
              No results for “{q}”
            </div>
          )}
          {(groups || []).map((g) => (
            <div key={g.title}>
              <div className="cmdk-group">{g.title}</div>
              {(g.items || []).map((it) => {
                running++;
                const a = running === idx;
                const me = flat.indexOf(it);
                const Icon = it.Icon;
                return (
                  <button data-testid="command-palette-button-1"
                    key={it.key}
                    className={`cmdk-item${a ? ' active' : ''}`}
                    onMouseEnter={() => setIdx(me)}
                    onClick={() => run(it)}
                  >
                    <span className="ci">
                      <Icon size={17} />
                    </span>
                    <span>{it.label}</span>
                    {a && <span className="cb kbd">↵</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
